import * as React from 'react';

import { DashboardCard, formatMoney } from '@rematter/ui-web';
import { useAtomValue } from 'jotai';

import {
  $overviewPendingBills,
  $overviewPendingInvoices,
} from '../../_state/financeOverview.state';
import { useBillStatusCountsQuery } from '../../hooks/useBillStatusCountsQuery';
import { useInvoiceStatusCountsQuery } from '../../hooks/useInvoiceStatusCountsQuery';
import { BillRecord, InvoiceRecord } from '../../types';

import { useTranslations } from '@/finance/i18n/useTranslations';

type CurrencyTotals = Record<string, number>;

const sumByCurrency = (
  items: Array<{ amount: { amount: number; currencyCode: string } }>,
): CurrencyTotals => {
  const totals: CurrencyTotals = {};
  for (const item of items) {
    const code = item.amount.currencyCode;
    totals[code] = (totals[code] ?? 0) + item.amount.amount;
  }
  return totals;
};

const formatCurrencyTotal = (
  totals: CurrencyTotals,
  otherCurrenciesLabel: (count: number) => string,
): string => {
  const entries = Object.entries(totals);
  if (entries.length === 0) return formatMoney(0, 'USD');
  if (entries.length === 1) return formatMoney(entries[0][1], entries[0][0]);

  const sorted = entries.sort((a, b) => b[1] - a[1]);
  const primary = sorted[0];
  const otherCount = sorted.length - 1;
  return `${formatMoney(primary[1], primary[0])} ${otherCurrenciesLabel(otherCount)}`;
};

export const OverviewKpiCards = () => {
  const t = useTranslations();
  const { data: billStatusCounts } = useBillStatusCountsQuery();
  const { data: invoiceStatusCounts } = useInvoiceStatusCountsQuery();
  const { data: pendingBillsData } = useAtomValue($overviewPendingBills);
  const { data: pendingInvoicesData } = useAtomValue($overviewPendingInvoices);

  const pendingBills: BillRecord[] = React.useMemo(
    () => pendingBillsData?.bills ?? [],
    [pendingBillsData],
  );

  const pendingInvoices: InvoiceRecord[] = React.useMemo(
    () => pendingInvoicesData?.invoices ?? [],
    [pendingInvoicesData],
  );

  const apTotals = React.useMemo(
    () => sumByCurrency(pendingBills),
    [pendingBills],
  );
  const arTotals = React.useMemo(
    () => sumByCurrency(pendingInvoices),
    [pendingInvoices],
  );

  const apDisplay = React.useMemo(
    () => formatCurrencyTotal(apTotals, t.overview.kpi.otherCurrencies),
    [apTotals, t],
  );

  const arDisplay = React.useMemo(
    () => formatCurrencyTotal(arTotals, t.overview.kpi.otherCurrencies),
    [arTotals, t],
  );

  const pendingBillsCount = billStatusCounts?.billStatusCounts?.pending ?? 0;
  const pendingInvoicesCount =
    invoiceStatusCounts?.invoiceStatusCounts?.pending ?? 0;

  return (
    <>
      <DashboardCard
        title={t.overview.kpi.pendingBills}
        value={pendingBillsCount.toString()}
        color="warning"
      />
      <DashboardCard
        title={t.overview.kpi.pendingInvoices}
        value={pendingInvoicesCount.toString()}
        color="primary"
      />
      <DashboardCard
        title={t.overview.kpi.totalAP}
        value={apDisplay}
        color="failure"
      />
      <DashboardCard
        title={t.overview.kpi.totalAR}
        value={arDisplay}
        color="success"
      />
    </>
  );
};
